import { GridColDef, GridPreProcessEditCellProps, GridRowId } from "@mui/x-data-grid"
import { Box } from "@mui/material"
import { useEffect, useState } from "react"
import EditableTable from "@components/EditableTable"
import {
  useAddPermissionMutation,
  useDeletePermissionMutation,
} from "@store/services/accessService"
import { useLazyGetUserPermissionsQuery } from "@store/services/userService"
import { ContractDto } from "@custom-types/contract"
import SelectContract from "@features/administration/contracts/SelectContract"
import ContractLabel from "@features/administration/contracts/SelectContract/ContractLabel"
import ContractTooltip from "@features/administration/contracts/SelectContract/ContractTooltip"
import SelectContractRoles from "@features/administration/contracts/SelectContractRole"

interface IPermissionsTableProps {
  email: string
  contracts: ContractDto[]
}

export type RowData = { contract: string; role: string; id: number }

export default function PermissionsTable({ email, contracts }: IPermissionsTableProps) {
  const [permissions, setPermissions] = useState<RowData[]>([])
  const [getUserPermissions, { data: userPermissions }] = useLazyGetUserPermissionsQuery()
  const [addPermission] = useAddPermissionMutation()
  const [deletePermissions] = useDeletePermissionMutation()

  const handleRowUpdate = async (row: RowData) => {
    const promises: Promise<string>[] = []

    if (row.id >= 0) {
      const toDelete = permissions.filter((p) => p.id === row.id)[0]
      const deletePromise = deletePermissions({
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        contract_key: toDelete.contract,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        role: toDelete.role,
        email,
      }).unwrap()
      promises.push(deletePromise)
    }

    const addPromise = addPermission({
      contract_key: row.contract,
      role: row.role,
      email,
    }).unwrap()
    promises.push(addPromise)

    await Promise.all(promises)
    getUserPermissions(encodeURIComponent(email))
  }

  const handleDeleteRow = (id: GridRowId | null) => {
    const foundPermission = permissions.find((x) => x.id === id)

    if (foundPermission) {
      deletePermissions({
        contract_key: foundPermission.contract,
        role: foundPermission.role,
        email,
      })
    }
  }

  const columns: GridColDef<RowData>[] = [
    {
      field: "contract",
      headerName: "Contract",
      headerClassName: "table-header-cell",
      flex: 1,
      editable: true,
      type: "singleSelect",
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value.length < 1
        return { ...params.props, error: hasError }
      },
      renderEditCell: (params) => {
        return (
          <SelectContract
            contracts={contracts}
            value={params.value}
            hasFocus={params.hasFocus}
            field={params.field}
            gridRowId={params.id}
          />
        )
      },
      renderCell: (params) => {
        const contract = contracts.find(({ key }) => key === params.value)

        return (
          <ContractTooltip contract={contract}>
            <Box sx={{ width: "100%" }}>
              <ContractLabel contractKey={params.value} />
            </Box>
          </ContractTooltip>
        )
      },
    },
    {
      field: "role",
      headerName: "Role",
      headerClassName: "table-header-cell",
      editable: true,
      type: "singleSelect",
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value.length < 1
        return { ...params.props, error: hasError }
      },
      renderEditCell: (params) => {
        return (
          <SelectContractRoles
            value={params.value}
            hasFocus={params.hasFocus}
            field={params.field}
            gridRowId={params.id}
          />
        )
      },
    },
  ]

  useEffect(() => {
    getUserPermissions(encodeURIComponent(email))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [email])

  useEffect(() => {
    if (userPermissions) {
      let counter = 0
      setPermissions(
        Object.entries(userPermissions).flatMap(([role, ids], i) =>
          ids.map((id) => {
            return {
              contract: id,
              role,
              id: counter++,
            }
          }),
        ),
      )
    }
  }, [userPermissions])

  const handleShouldDeleteRow = (row: RowData) => {
    const hasError = !row.contract || !row.role
    const isNewRow = row.id < 0

    return isNewRow && hasError
  }

  return (
    <Box className={"permissions-table-container"}>
      <EditableTable
        isEditable
        data={permissions}
        setData={setPermissions}
        columns={columns}
        onRowUpdate={handleRowUpdate}
        shouldDeleteRow={handleShouldDeleteRow}
        onDelete={handleDeleteRow}
        emptyObject={{ contract: "", role: "" }}
        fieldToFocus={"contract"}
      />
    </Box>
  )
}
